<template>
	<div class="leaflet-map-wrapper">
		<label v-if="!isViewOnly">
			<b>Click map to set latitude and longitude automatically</b>
		</label>

		<l-map
			class="leaflet-map position-relative"
			:zoom="zoom"
			:center="center"
			:options="mapOptions"
			@click="addMarkerOnClick"
		>
			<l-tile-layer :url="url" />

			<l-marker v-if="markerCoords" :lat-lng="markerCoords">
				<l-tooltip :options="{ permanent: true, interactive: true }">
					<div>
						{{ markerCoords }}
					</div>
				</l-tooltip>
			</l-marker>

			<!-- Search Box -->
			<div class="search-container position-absolute" v-on:click.stop v-if="!isViewOnly">
				<div class="input-group">
					<input
						type="text"
						class="input form-control"
						style="border-right: 0 solid white; border-top-left-radius: 24px !important;"
						:style="{ 'border-bottom-left-radius': searchResultsExist ? '0px !important' : '24px !important' }"
						v-model="searchQuery"
						v-prevent-double-click
						placeholder="Search Location"
						v-on:keyup.enter="searchPlace"
					>
					<div class="input-group-append">
						<span
							@click="searchPlace"
							class="input-group-text bg-white cursor-pointer"
							style="border-left: 0 solid white !important; border-top-right-radius: 24px !important; border-width: 2px 2px 2px 0; border-style: solid; border-color: rgba(0,0,0,0.2); font-size: 0.75rem;"
							:style="{ 'border-bottom-right-radius': searchResultsExist ? '0px !important' : '24px !important' }"
						>
							<i class="bi bi-search" :class="{ 'is-loading': isSearching }"></i>
						</span>
					</div>
				</div>

				<ul v-if="searchResultsExist" class="search-results">
					<li
						v-for="result in searchResults"
						:key="result.place_id"
						@click="setLocation(result)"
					>
						{{ result.display_name }}
					</li>
				</ul>
			</div>

			<button
				v-if="!isViewOnly"
				class="btn btn-light bg-white position-absolute geolocation"
				v-on:click.stop
				@click="geolocate(true)"
			>
				<i class="bi bi-crosshair"></i>
			</button>
		</l-map>

		<!-- Modal Geolocation Error -->
		<modal
			class="table-filter-modal"
			size="medium"
			@close="isShowGeolocationModal = false"
			v-if="isShowGeolocationModal">
			<div slot="header">
				<h3>Oops! We need access to your location</h3>
			</div>

			<div slot="body">
				<p>To provide you with the best experience, we require access to your location. Unfortunately, it seems you've denied access.</p>
				<p>
					<b>How to enable location access:</b>
					<ol>
						<li><b>Click on the icon</b> next to the website address in your browser's address bar.</li>
						<li><b>Find "Location"</b> in the dropdown menu.</li>
						<li><b>Select "Allow"</b> from the dropdown menu next to Location.</li>
					</ol>
				</p>
				<p>Once you've allowed access, please refresh the page to continue.</p>
			</div>

			<div slot="footer">
				<button
					type="button"
					class="btn btn-light"
					@click="isShowGeolocationModal = false"
				>
					{{ $t('Close') }}
				</button>
			</div>
		</modal>
	</div>
</template>

<script>
import { latLng, Icon } from 'leaflet';
import { LMap, LTileLayer, LMarker, LTooltip } from 'vue2-leaflet';
import { searchPlace } from '@/api/utilities';
import Modal from '@/components/Modal.vue';

export default {
	name: 'LeafletMap',
	components: {
		LMap,
		LTileLayer,
		LMarker,
		LTooltip,
		Modal,
	},
	props: {
		lat: {},
		lng: {},
		isViewOnly: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			lastValidLat: null,
			lastValidLng: null,
			zoom: 14,
			center: latLng(-6.914744, 107.609810), // Default to Bandung
			url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
			mapOptions: {
				zoomSnap: 0.5,
			},
			markerCoords: null,
			searchQuery: '',
			searchResults: [],
			isSearching: false,
			isShowGeolocationModal: false,
		};
	},
	methods: {
		fixLeafletMissingIcons() {
			delete Icon.Default.prototype._getIconUrl;
			Icon.Default.mergeOptions({
				// eslint-disable-next-line global-require
				iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
				// eslint-disable-next-line global-require
				iconUrl: require('leaflet/dist/images/marker-icon.png'),
				// eslint-disable-next-line global-require
				shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
			});
		},
		addMarkerOnClick(event) {
			if (this.isViewOnly) {
				return;
			}
			const lat = event?.latlng?.lat ?? '';
			const lng = event?.latlng?.lng ?? '';

			this.updateLocation(lat, lng);
		},
		geolocate(force = false) {
			this.isSearching = true;

			const successCallback = (position) => {
				const lat = position?.coords?.latitude ?? '';
				const lng = position?.coords?.longitude ?? '';

				this.updateLocation(lat, lng);

				this.isSearching = false;
			};

			const errorCallback = (e) => {
				console.log(e);

				if (force) {
					this.isShowGeolocationModal = true;
				}

				this.isSearching = false;
			};

			navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
		},
		initLocation() {
			if (this.lat && this.lng) {
				this.updateLocation(this.lat, this.lng);
			} else {
				this.geolocate();
			}
		},
		searchPlace() {
			if (!this.searchQuery) {
				this.searchResults = [];
				return;
			}

			this.isSearching = true;

			const params = {
				format: 'json',
				q: encodeURIComponent(this.searchQuery),
				limit: 5,
				countrycodes: 'id',
			};

			const callback = (response) => {
				this.searchResults = response;

				this.isSearching = false;
			};

			const errorCallback = (e) => {
				console.log(e);

				this.isSearching = false;
			};

			searchPlace(params, callback, errorCallback);
		},
		setLocation(place) {
			const lat = place.lat ?? '';
			const lng = place.lon ?? '';

			this.updateLocation(lat, lng);

			this.searchResults = [];
			this.searchQuery = '';
		},
		updateLocation(lat, lng) {
			const newCoordinate = latLng(lat, lng);
			this.markerCoords = newCoordinate;
			this.center = newCoordinate;
			this.lastValidLat = lat;
			this.lastValidLng = lng;
			this.$emit('set-location', { lat, lng });
		},
	},
	computed: {
		searchResultsExist() {
			return this.searchResults.length > 0 && this.searchQuery !== '';
		},
	},
	mounted() {
		this.fixLeafletMissingIcons();
		this.initLocation();
	},
	watch: {
		searchQuery() {
			this.ooDelay(() => {
				this.searchPlace();
			}, 1000);
		},
		lat() {
			this.ooDelay(() => {
				try {
					this.updateLocation(this.lat, this.lng);
				} catch (error) {
					console.log(error?.message);
					this.updateLocation(this.lastValidLat, this.lng);
				}
			}, 1000);
		},
		lng() {
			this.ooDelay(() => {
				try {
					this.updateLocation(this.lat, this.lng);
				} catch (error) {
					console.log(error?.message);
					this.updateLocation(this.lat, this.lastValidLng);
				}
			}, 1000);
		},
	},
	directives: {
		'prevent-double-click': {
			inserted(el) {
				el.addEventListener('dblclick', (event) => {
					event.stopPropagation();
					event.preventDefault();
				});
			},
		},
	},
};
</script>

<style scoped>
.leaflet-map {
	height: 400px;
	width: 100%;
}

.search-container {
	position: relative;
	width: 50%;
	margin: 0 auto;
	top: 10px;
	z-index: 1000;
	left: 50%;
	transform: translateX(-50%);
}

input[type="text"] {
	width: 100%;
	padding: 10px;
	font-size: 16px;
	border-radius: 5px;
	border: 2px solid rgba(0,0,0,0.2);
}

.search-results {
	position: absolute;
	top: 100%;
	left: 0;
	width: 100%;
	background-color: #fff;
	list-style-type: none;
	padding: 0;
	margin: 0;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-results li {
	padding: 10px;
	border-bottom: 1px solid #ccc;
}

.search-results li:last-child {
	border-bottom: none;
}

.search-results li:hover {
	background-color: #f9f9f9;
	cursor: pointer;
}

.input-group {
	flex-wrap: nowrap;
}

.geolocation {
	bottom: 10px;
	left: 10px;
	z-index: 1000;
	border: 2px solid rgba(0,0,0,0.2);
}
</style>
